import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { glass } from '../../theme';

/// One row of a home screen: a heading, a strip that scrolls sideways, and a pair of buttons
/// that page through it for anybody without a trackpad or a touch screen. The strip itself has
/// no scrollbar - the buttons are its scrollbar, and a grey bar under every row is noise.
export default function Shelf({
  title,
  columnWidth,
  gap = 2.5,
  children,
}: {
  title: string;
  /// How wide one tile is from a tablet up; phones fit two and a bit, whatever this says.
  columnWidth: number;
  gap?: number;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const strip = useRef<HTMLDivElement>(null);
  const [ends, setEnds] = useState({ start: true, end: true });

  // Forward is towards the end of the writing, which in a right-to-left layout is leftwards -
  // and so is a negative scroll position.
  const forward = theme.direction === 'rtl' ? -1 : 1;

  const measure = useCallback(() => {
    const element = strip.current;

    if (!element) {
      return;
    }

    const travelled = Math.abs(element.scrollLeft);
    const room = element.scrollWidth - element.clientWidth;

    setEnds({ start: travelled <= 2, end: travelled >= room - 2 });
  }, []);

  useEffect(() => {
    const element = strip.current;

    if (!element) {
      return undefined;
    }

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(element);
    element.addEventListener('scroll', measure, { passive: true });

    return () => {
      observer.disconnect();
      element.removeEventListener('scroll', measure);
    };
  }, [measure]);

  const page = (direction: 1 | -1) => {
    const element = strip.current;

    if (element) {
      element.scrollBy({ left: direction * forward * element.clientWidth * 0.85, behavior: 'smooth' });
    }
  };

  const arrow = {
    ...glass(theme.palette.mode, 'thin'),
    width: 34,
    height: 34,
    border: '1px solid',
    borderColor: 'divider',
    '&:hover': { borderColor: theme.palette.text.secondary },
    '&.Mui-disabled': { opacity: 0.35 },
  };

  const [BackIcon, NextIcon] = theme.direction === 'rtl'
    ? [ChevronRightIcon, ChevronLeftIcon]
    : [ChevronLeftIcon, ChevronRightIcon];

  return (
    <Stack spacing={0.5}>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
        <Typography variant="h3" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
          {title}
        </Typography>

        {/* Only where there is somewhere to go: a row that fits needs no buttons. */}
        {!(ends.start && ends.end) && (
          <Stack direction="row" spacing={1} sx={{ display: { xs: 'none', sm: 'flex' } }}>
            <IconButton size="small" sx={arrow} disabled={ends.start} onClick={() => page(-1)} aria-label={t('common.back')}>
              <BackIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" sx={arrow} disabled={ends.end} onClick={() => page(1)} aria-label={t('common.next')}>
              <NextIcon fontSize="small" />
            </IconButton>
          </Stack>
        )}
      </Stack>

      <Box
        ref={strip}
        sx={{
          display: 'grid',
          gridAutoFlow: 'column',
          gridAutoColumns: { xs: '42%', sm: '28%', md: `${columnWidth}px` },
          gap,
          overflowX: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },

          // The strip runs off the edge of the window rather than stopping at the padding,
          // which is what says there is more of it than fits.
          marginInlineEnd: { xs: -2, md: -4 },
          paddingInlineEnd: { xs: 2, md: 4 },

          // A strip that scrolls sideways clips whatever leaves its box on every edge, and the
          // tiles come forward with a ring around them when chosen. The room they need is
          // padding here, taken back out of the layout with a margin on the side it starts.
          marginInlineStart: -1,
          paddingInlineStart: 1,
          pt: 1.5,
          pb: 1.5,
          scrollSnapType: 'x proximity',
          scrollPaddingInline: 8,
          '& > *': { scrollSnapAlign: 'start' },
        }}
      >
        {children}
      </Box>
    </Stack>
  );
}
